import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from } from 'rxjs';
import { delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { ApiService } from '../../services/api/api.service';
import * as actions from '../actions/tags.actions';
import { getCurrentOrganizationId$ } from '../selectors';
import { getAssetsTags$, getTagsObject$ } from '../selectors/tags.selector';

@Injectable()
export class TagsEffects {
    constructor(private actions$: Actions, private _api: ApiService, private store$: Store) {}

    getTaggedApps = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetTagAppsByKeyValues),
            delay(10),
            withLatestFrom(this.store$.select(getTagsObject$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, apps, organizationId]) => {
                const taggedApps = apps?.[`${action.tagKey}}|APPROVED`] || [];
                if (taggedApps.length > 0 || !action.tagKey) {
                    return [];
                }
                if (!(organizationId?.length > 0)) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetTagAppsByKeyValues(action));
                    }, 100);
                    return [];
                }
                let method: Promise<
                    {
                        metadataId: string;
                        metadataName: string;
                        metadataType: string;
                        repositoryId: string;
                        serviceId: string;
                        restApiName: string;
                        tagKey: string;
                        tagValue: string;
                    }[]
                >;
                if (action.tagValue?.length > 0) {
                    method = this._api.tags.getTaggedApps(organizationId, action.tagKey, action.tagValue, action.noAlerts, [
                        'ASSET',
                        'BRIDGEASSET',
                        'WIDGET',
                        'DASHBOARD',
                        'RECON',
                    ]);
                } else {
                    const key = action.tagKey + (action.tagValue?.length > 0 ? `:${action.tagValue}` : '');
                    method = this._api.assets.getServicesByTagValues({
                        key: key,
                        serviceTypes: ['ASSET', 'BRIDGEASSET', 'WIDGET', 'DASHBOARD', 'RECON'],
                        subscriptionStatus: 'APPROVED',
                    });
                }
                return from(method).pipe(
                    map(response =>
                        actions.SetTagAppsByKeyValues({
                            apps: response,
                            status: 'APPROVED',
                            tagKey: action.tagKey,
                            tagValue: action.tagValue,
                            organizationId,
                        })
                    )
                );
            })
        );
    });

    getAssetTags = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAssetTags),
            delay(10),
            withLatestFrom(this.store$.select(getAssetsTags$)),
            mergeMap(([action, tags]) => {
                const appTags = tags?.[action.serviceId];
                if (Object.keys(appTags || {})?.length > 0) {
                    return [];
                }
                return from(this._api.tags.getAssetTagKeyValues(action.serviceId)).pipe(
                    map(tags =>
                        actions.SetAssetTags({
                            serviceId: action.serviceId,
                            tagKeyValues: tags,
                        })
                    )
                );
            })
        );
    });
}
